import { Button } from '@/components/ui/button';
import {
    Dialog,
    DialogContent,
    DialogDescription,
    DialogHeader,
    DialogTitle,
    DialogTrigger,
} from '@/components/ui/dialog';
import { useTranslations } from '@/hooks/use-translations';
import { usePage } from '@inertiajs/react';
import { Check, Facebook, Instagram, Link2, Linkedin, Mail, Share2 } from 'lucide-react';
import { ReactNode, useState } from 'react';

interface SocialSharingProps {
    url: string;
    title: string;
    description?: string;
    className?: string;
}

interface ShareOption {
    name: string;
    icon: ReactNode;
    action: () => void;
}

function useShareOptions(url: string, title: string, description: string = ''): {
    shareOptions: ShareOption[];
    shareText: string;
    copiedPlatform: string | null;
    setCopiedPlatform: (platform: string | null) => void;
} {
    const [copiedPlatform, setCopiedPlatform] = useState<string | null>(null);
    const { t } = useTranslations();
    const { sections } = usePage().props as any;

    // Try to get iconic quote for more engaging share text
    const iconicQuote = sections?.['iconic-quote']?.content;
    
    // Standardized share text - same for all platforms
    const baseShareText = t('share_book_text', { title });
    
    // Add iconic quote if available
    const shareText = iconicQuote 
        ? `"${iconicQuote.slice(0, 150)}..." - ${baseShareText}`
        : baseShareText;

    const encodedUrl = encodeURIComponent(url);
    const encodedTitle = encodeURIComponent(title);
    const encodedShareText = encodeURIComponent(shareText);
    const encodedDescription = encodeURIComponent(description || shareText);

    const shareOptions: ShareOption[] = [
        {
            name: 'X (Twitter)',
            icon: (
                <svg className="h-5 w-5" viewBox="0 0 24 24" fill="currentColor">
                    <path d="M18.244 2.25h3.308l-7.227 8.26 8.502 11.24H16.17l-5.214-6.817L4.99 21.75H1.68l7.73-8.835L1.254 2.25H8.08l4.713 6.231zm-1.161 17.52h1.833L7.084 4.126H5.117z" />
                </svg>
            ),
            action: () => {
                window.open(
                    `https://twitter.com/intent/tweet?url=${encodedUrl}&text=${encodedShareText}`,
                    '_blank',
                    'width=600,height=400'
                );
            },
        },
        {
            name: 'Facebook',
            icon: <Facebook className="h-5 w-5" />,
            action: () => {
                window.open(
                    `https://www.facebook.com/sharer/sharer.php?u=${encodedUrl}&quote=${encodedShareText}`,
                    '_blank',
                    'width=600,height=400'
                );
            },
        },
        {
            name: 'LinkedIn',
            icon: <Linkedin className="h-5 w-5" />,
            action: () => {
                window.open(
                    `https://www.linkedin.com/sharing/share-offsite/?url=${encodedUrl}&title=${encodedTitle}&summary=${encodedDescription}`,
                    '_blank',
                    'width=600,height=400'
                );
            },
        },
        {
            name: 'Pinterest',
            icon: (
                <svg className="h-5 w-5" viewBox="0 0 24 24" fill="currentColor">
                    <path d="M12 0C5.373 0 0 5.372 0 12c0 5.084 3.163 9.426 7.627 11.174-.105-.949-.2-2.405.042-3.441.218-.937 1.407-5.965 1.407-5.965s-.359-.719-.359-1.782c0-1.668.967-2.914 2.171-2.914 1.023 0 1.518.769 1.518 1.69 0 1.029-.655 2.568-.994 3.995-.283 1.194.599 2.169 1.777 2.169 2.133 0 3.772-2.249 3.772-5.495 0-2.873-2.064-4.882-5.012-4.882-3.414 0-5.418 2.561-5.418 5.207 0 1.031.397 2.138.893 2.738a.36.36 0 01.083.345l-.333 1.36c-.053.22-.174.267-.402.161-1.499-.698-2.436-2.889-2.436-4.649 0-3.785 2.75-7.262 7.929-7.262 4.163 0 7.398 2.967 7.398 6.931 0 4.136-2.607 7.464-6.227 7.464-1.216 0-2.359-.631-2.75-1.378l-.748 2.853c-.271 1.043-1.002 2.35-1.492 3.146C9.57 23.812 10.763 24 12 24c6.627 0 12-5.373 12-12 0-6.628-5.373-12-12-12z" />
                </svg>
            ),
            action: () => {
                window.open(
                    `https://pinterest.com/pin/create/button/?url=${encodedUrl}&description=${encodedShareText}`,
                    '_blank',
                    'width=600,height=400'
                );
            },
        },
        {
            name: 'Instagram',
            icon: <Instagram className="h-5 w-5" />,
            action: () => {
                navigator.clipboard.writeText(`${shareText}\n\n${url}`);
                setCopiedPlatform('instagram');
                setTimeout(() => setCopiedPlatform(null), 2000);
            },
        },
        {
            name: 'Email',
            icon: <Mail className="h-5 w-5" />,
            action: () => {
                window.location.href = `mailto:?subject=${encodeURIComponent(
                    t('share_email_subject', { title })
                )}&body=${encodedShareText}%0A%0A${encodedUrl}`;
            },
        },
        {
            name: 'Copy Link',
            icon: <Link2 className="h-5 w-5" />,
            action: () => {
                navigator.clipboard.writeText(url);
                setCopiedPlatform('link');
                setTimeout(() => setCopiedPlatform(null), 2000);
            },
        },
    ];

    return { shareOptions, shareText, copiedPlatform, setCopiedPlatform };
}

export function SocialSharing({ url, title, description = '', className = '' }: SocialSharingProps) {
    const { t } = useTranslations();
    const { shareOptions, shareText, copiedPlatform } = useShareOptions(url, title, description);

    return (
        <Dialog>
            <DialogTrigger asChild>
                <Button variant="default" size="default" className={`gap-2 px-6 py-2 text-base ${className}`}>
                    <Share2 className="h-5 w-5" />
                    <span>{t('Share')}</span>
                </Button>
            </DialogTrigger>
            <DialogContent className="sm:max-w-md">
                <DialogHeader>
                    <DialogTitle>{t('share_this_book')}</DialogTitle>
                    <DialogDescription>{t('share_choose_platform', { title })}</DialogDescription>
                </DialogHeader>
                <div className="grid grid-cols-3 gap-4 py-4">
                    {shareOptions.map((option) => (
                        <button
                            key={option.name}
                            onClick={option.action}
                            className="group flex flex-col items-center gap-2 rounded-lg p-3 transition-colors hover:bg-muted"
                        >
                            <div className="flex h-12 w-12 items-center justify-center rounded-full bg-background shadow-sm ring-1 ring-border group-hover:ring-2 group-hover:ring-primary">
                                {copiedPlatform === option.name.toLowerCase().replace(' ', '-') ||
                                (option.name === 'Copy Link' && copiedPlatform === 'link') ||
                                (option.name === 'Instagram' && copiedPlatform === 'instagram') ? (
                                    <Check className="h-5 w-5 text-green-600" />
                                ) : (
                                    option.icon
                                )}
                            </div>
                            <span className="text-xs font-medium text-muted-foreground group-hover:text-foreground">
                                {option.name}
                            </span>
                        </button>
                    ))}
                </div>
                <div className="rounded-lg border bg-muted/50 p-3">
                    <p className="text-xs text-muted-foreground">
                        {shareText}
                    </p>
                </div>
            </DialogContent>
        </Dialog>
    );
}

// Compact variant for tight spaces with outline style
export function SocialSharingCompact({ url, title, description = '', className = '' }: SocialSharingProps) {
    const { t } = useTranslations();
    const { shareOptions, shareText, copiedPlatform } = useShareOptions(url, title, description);

    return (
        <Dialog>
            <DialogTrigger asChild>
                <Button variant="outline" size="sm" className={`gap-2 px-4 py-1.5 text-sm ${className}`}>
                    <Share2 className="h-4 w-4" />
                    <span>{t('Share')}</span>
                </Button>
            </DialogTrigger>
            <DialogContent className="sm:max-w-md">
                <DialogHeader>
                    <DialogTitle>{t('share_this_book')}</DialogTitle>
                    <DialogDescription>{t('share_choose_platform', { title })}</DialogDescription>
                </DialogHeader>
                <div className="grid grid-cols-3 gap-4 py-4">
                    {shareOptions.map((option) => (
                        <button
                            key={option.name}
                            onClick={option.action}
                            className="group flex flex-col items-center gap-2 rounded-lg p-3 transition-colors hover:bg-muted"
                        >
                            <div className="flex h-12 w-12 items-center justify-center rounded-full bg-background shadow-sm ring-1 ring-border group-hover:ring-2 group-hover:ring-primary">
                                {copiedPlatform === option.name.toLowerCase().replace(' ', '-') ||
                                (option.name === 'Copy Link' && copiedPlatform === 'link') ||
                                (option.name === 'Instagram' && copiedPlatform === 'instagram') ? (
                                    <Check className="h-5 w-5 text-green-600" />
                                ) : (
                                    option.icon
                                )}
                            </div>
                            <span className="text-xs font-medium text-muted-foreground group-hover:text-foreground">
                                {option.name}
                            </span>
                        </button>
                    ))}
                </div>
                <div className="rounded-lg border bg-muted/50 p-3">
                    <p className="text-xs text-muted-foreground">
                        {shareText}
                    </p>
                </div>
            </DialogContent>
        </Dialog>
    );
}

// Large variant for prominent CTAs
export function SocialSharingLarge({ url, title, description = '' }: SocialSharingProps) {
    return (
        <SocialSharing
            url={url}
            title={title}
            description={description}
            className="!px-8 !py-3 !text-lg [&>svg]:!h-6 [&>svg]:!w-6"
        />
    );
}